import { Button, Dropdown, Input, Select, Space } from "antd";
import { useLazySearchDebtorsByTypeQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { DEBTOR_SEARCH_MIN_CHARS } from "common/constants";
import { useDebtorSearchCriteriaOptions } from "features/debtorProfile/hooks/useDebtorSearchCriteriaOptions";
import { collectorFullName } from "features/home/utils";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useState } from "react";
import styled from "styled-components";

const StyledSpace = styled(Space.Compact)`
  width: 100%;
`;

const MAX_VISIBLE_ACCOUNT_IDS = 5;

function DebtorSelector({ onSelectDebtor, selectedDebtors = [] }) {
  const [queryFieldType, setQueryFieldType] = useState("consumer_name");
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { data: constants, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const { options: debtorSearchCriteriaOptions, isLoading: isDebtorCustomFieldsLoading } =
    useDebtorSearchCriteriaOptions(constants?.debtorSearchCriteria);
  const [searchDebtors, { data: searchResults, isFetching }] = useLazySearchDebtorsByTypeQuery();

  const selectedDebtorIds = selectedDebtors.map((debtor) => debtor.id) ?? [];

  const handleSearch = async (value) => {
    if (value && value.trim().length >= DEBTOR_SEARCH_MIN_CHARS) {
      await searchDebtors({ query: value.trim(), queryFieldType });
      setIsDropdownOpen(true);
    }
  };

  const handleAddDebtor = (debtor) => {
    if (!selectedDebtorIds.includes(debtor.id)) {
      onSelectDebtor(debtor);
      setIsDropdownOpen(false);
      setQuery("");
    }
  };

  const handleInputKeyDown = (e) => {
    // Prevent form submission when pressing Enter
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSearch(query);
    }
    // Close dropdown on Escape
    if (e.key === "Escape") {
      setIsDropdownOpen(false);
    }
  };

  const items =
    searchResults?.map((debtor) => ({
      key: debtor.id,
      label: (
        <>
          <span>
            {collectorFullName(debtor)} (Debtor ID: {debtor.id}, Account IDs: [
            {debtor.accountIds?.slice(0, MAX_VISIBLE_ACCOUNT_IDS).join(", ")}
            {debtor.accountIds?.length > MAX_VISIBLE_ACCOUNT_IDS && "..."}])
          </span>
          <Button
            type="link"
            disabled={selectedDebtorIds.includes(debtor.id)}
            onClick={() => handleAddDebtor(debtor)}
          >
            {selectedDebtorIds.includes(debtor.id) ? "Added" : "Add"}
          </Button>
        </>
      ),
    })) ?? [];

  return (
    <Dropdown
      menu={{ items }}
      open={isDropdownOpen && items.length > 0}
      onOpenChange={(open) => setIsDropdownOpen(open)}
      trigger={["click"]}
    >
      <StyledSpace>
        <Select
          value={queryFieldType}
          onChange={(value) => {
            setQueryFieldType(value);
            setQuery("");
            setIsDropdownOpen(false);
          }}
          options={debtorSearchCriteriaOptions}
          loading={isConstantsLoading || isDebtorCustomFieldsLoading}
          popupMatchSelectWidth={false}
          allowClear
          maxTagTextLength={20}
          style={{ minWidth: 150 }}
        />
        <Input.Search
          placeholder="Debtor Search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!e.target.value) {
              setIsDropdownOpen(false);
            }
          }}
          onKeyDown={handleInputKeyDown}
          onSearch={handleSearch}
          loading={isFetching}
          allowClear
          enterButton
          style={{ flex: 1 }}
        />
      </StyledSpace>
    </Dropdown>
  );
}

export default DebtorSelector;
